import { useEffect, useRef, useState } from "react";
import { Config } from "../config/Config";
import { aiStateTypes } from "../types/ai/states";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { isModelUnavailableStatus, notifyModelUnavailable } from "../components/ui/ModelUnavailableNotice";
import { useTrackedRun, TrackedRunHandle } from "./useTrackedRun";
import { RunType } from "../apis/ApiPortalBackend";

//TODO: Load Chat Massages for user from database
export const useAiImageTypesGeneration = () : [
    generation:any,
    state: aiStateTypes,
    setState: (state:aiStateTypes) => void,
    getJobID: (runType: RunType, path:string,body:any,specificImageIDsOnly?:number[]|null) => void,
    insufficientCreditsDialog: JSX.Element | null
] => {
    const [jobID, setJobID] = useState<null|string>(null);
    const [specificImageIDs, setSpecificImageIDs] = useState<number[]|null>(null);
    const [state, setState] = useState<aiStateTypes>(null)
    const baseUrl = Config.json.AiServiceProxyUrl;
    const [generation, setGeneration] = useState<any>([]);

    const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

    // Tracks which job the currently active poll loop belongs to. Without this, a poll loop that
    // never reaches a terminal status (e.g. the job expires server-side and /status starts
    // returning something that falls through to "unbekannt") keeps rescheduling itself via
    // setTimeout forever, completely outside the React lifecycle - it doesn't stop when a new
    // generation starts or the component would otherwise consider itself idle. That zombie loop
    // then keeps calling setState/setJobID with stale results, which can stomp on a later,
    // unrelated generation's state. A poll checks this ref before touching any state; only a
    // full page reload used to reliably kill these orphaned timers.
    const activeJobIDRef = useRef<string | null>(null);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Billing run tied to the currently active job - completed/failed once the job reaches a
    // terminal status (see poll()).
    const runHandleRef = useRef<TrackedRunHandle | null>(null);
    const runCostParamsRef = useRef<{ width?: number; height?: number }>({});

    const getJobID = async(runType: RunType, path:string,body:any,specificImageIDsOnly:number[]|null=null) =>{
        const handle = await startRun(runType, { estimatedCost: 0 });
        if (!handle) {
            // Either insufficient balance (dialog shown by useTrackedRun) or another failure -
            // fail closed, don't call cProxy without a tracked run.
            setState('error');
            return;
        }
        runHandleRef.current = handle;
        runCostParamsRef.current = { width: body?.width, height: body?.height };

        // 1. request: generate image
        const res = await fetchWithAuth(`${baseUrl}/${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        }).catch(err => {return null;});

        if (res && isModelUnavailableStatus(res.status)) {
            // Modell in dieser Umgebung gesperrt (cProxy-Modell-Policy) - freundlicher Hinweis,
            // Ruhezustand statt rotem Fehler.
            failRun(handle, "Model not available in this environment (HTTP 403)");
            setState('unavailable');
            notifyModelUnavailable();
            return;
        }
        if (!res || !res.ok) {
            setState('error');
            failRun(handle, `Generation request failed${res ? ` (HTTP ${res.status})` : ""}`);
            return;
        }
        const data = await res.json();
        const jobId = data.job_id;

        setSpecificImageIDs(specificImageIDsOnly);
        setJobID(jobId);
    };
    const poll = async(forJobID: string) => {
        if (activeJobIDRef.current !== forJobID) return;

        // 2. request: get state (waiting or finished or error)
        const statusRes = await fetchWithAuth(`${baseUrl}/status/${forJobID}`);
        const statusData = await statusRes.json();
        const status = statusData.status || "unbekannt";

        if (activeJobIDRef.current !== forJobID) return;
        setState(status);

        if (status === "finished") {
            // 3. request: get result
          const resultRes = await fetchWithAuth(`${baseUrl}/result/${forJobID}`);

          // resultData is array of urls
          const resultData = await resultRes.json();

          if (activeJobIDRef.current !== forJobID) return;

          if (resultData.length > 0 && !specificImageIDs) {
            setGeneration(resultData);
            setState('done');
            setJobID(null);
            completeRun(runHandleRef.current, runCostParamsRef.current);
          }
          else if(specificImageIDs && specificImageIDs.length > 0 && resultData.length > 0){
            //O: mask, 1: image
            const responseImages = specificImageIDs.map(index => resultData[index]).filter(image => image !== undefined);
            setGeneration(responseImages);
            setState('done');
            setJobID(null);
            completeRun(runHandleRef.current, runCostParamsRef.current);
          }
          else {
            setState('error');
            setJobID(null);
            failRun(runHandleRef.current, "Generation finished but returned no results");
          }
        } else if (status === "timeout" || status === "error") {
            setState('error');
            setJobID(null);
            failRun(runHandleRef.current, `Generation ${status}`);
        } else {
          pollTimeoutRef.current = setTimeout(() => poll(forJobID), 2000);
        }
    };
    useEffect(()=>{
        activeJobIDRef.current = jobID;
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        if(jobID !== null){
            poll(jobID);
        }
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };
    },[jobID])

    return [generation,state,setState,getJobID,insufficientCreditsDialog];
};
